import React, { useMemo, useState } from 'react'
import { Button, Drawer, Typography } from 'antd'
import { useFFEStore } from '../../store/ffeStore'
import type { LineItem, Material, Status } from '../../types/ffe'
import { MaterialSearch } from './MaterialSearch'
import { MaterialTotal } from './MaterialTotal'
import { MaterialCard } from './MaterialCard'
import { MaterialEditDrawer } from './MaterialEditDrawer'
import { MaterialForm } from './MaterialForm'
import { AccessoriesForm } from './AccessoriesForm'

const { Text } = Typography

interface Props {
  lineItem: LineItem
  addButton: boolean
  accessories: boolean
}

export const MaterialsList: React.FC<Props> = ({
  lineItem,
  addButton,
  accessories
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(
    null
  )
  const materials = useFFEStore((state) => state.document.materials)
  const updateLineItem = useFFEStore((state) => state.updateLineItem)
  const assignMaterialToLineItem = useFFEStore(
    (state) => state.assignMaterialToLineItem
  )
  const removeMaterialFromLineItem = useFFEStore(
    (state) => state.removeMaterialFromLineItem
  )
  const updateLineItemMaterialQuantity = useFFEStore(
    (state) => state.updateLineItemMaterialQuantity
  )
  const updateLineItemMaterialHideOnDocument = useFFEStore(
    (state) => state.updateLineItemMaterialHideOnDocument
  )
  const [addingNewMaterial, setAddingNewMaterial] = useState(false)
  const materialType = accessories ? 'accessory' : 'finish'

  const changeMaterialStatus = (
    lineItemId: string,
    materialId: string,
    status: Status
  ) => {
    const material = lineItem.materials?.find(
      (m) => m.materialId === materialId
    )
    if (!material) return
    updateLineItem(lineItemId, {
      materials: (lineItem.materials || []).map((m) =>
        m.materialId === materialId ? { ...m, status } : m
      )
    })
  }

  const sortMaterials = (mats: Array<Material>) => {
    return mats.sort((a, b) => {
      const aSelected = lineItem.materials?.some((m) => m.materialId === a.id)
      const bSelected = lineItem.materials?.some((m) => m.materialId === b.id)
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      return 0
    })
  }

  const filteredMaterials = useMemo(() => {
    if (!searchTerm) return sortMaterials(materials)
    const term = searchTerm.toLowerCase()
    const list = materials.filter(
      (material) =>
        (material.name || '').toLowerCase().includes(term) ||
        (material.type || '').toLowerCase().includes(term)
    )
    // sort by selected first
    return sortMaterials(list)
  }, [materials, searchTerm])

  const totalMaterialsSell = useMemo(() => {
    return (
      lineItem.materials
        ?.filter((material) => material.materialType === materialType)
        ?.reduce((acc, mat) => {
          const material = materials.find((m) => m.id === mat.materialId)
          if (!material) return acc
          return acc + material.sellPricePerUnit * mat.quantity
        }, 0) || 0
    )
  }, [lineItem.materials, materials])

  const totalMargin = useMemo(() => {
    if (!lineItem.materials?.length) return 0

    const totalCost = lineItem.materials
      .filter((material) => material.materialType === materialType)
      .reduce((acc, mat) => {
        const material = materials.find((m) => m.id === mat.materialId)
        if (!material) return acc
        return acc + material.costPerUnit * mat.quantity
      }, 0)

    const totalSell = totalMaterialsSell
    if (totalCost === 0) return 0
    return ((totalSell - totalCost) / totalCost) * 100
  }, [lineItem.materials, materials, totalMaterialsSell])

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-between gap-4">
          <MaterialSearch
            accessories={accessories}
            value={searchTerm}
            onChange={setSearchTerm}
          />
          {addButton && (
            <Button
              type="primary"
              className="btn btn-primary"
              onClick={() => setAddingNewMaterial(true)}
            >
              Add New {accessories ? 'Accessory' : 'Finish'}
            </Button>
          )}
        </div>

        {filteredMaterials
          .filter((material) => material.materialType === materialType)
          .map((material) => (
            <MaterialCard
              lineItem={lineItem}
              accessories={accessories}
              key={material.id}
              material={material}
              lineItemMaterial={lineItem.materials?.find(
                (m) => m.materialId === material.id
              )}
              onAssign={(quantity) =>
                assignMaterialToLineItem(
                  lineItem.id,
                  material.id,
                  quantity,
                  materialType
                )
              }
              onChangeHideOnDocument={(value) =>
                updateLineItemMaterialHideOnDocument(
                  lineItem.id,
                  material.id,
                  value
                )
              }
              onChangeStatus={(status) =>
                changeMaterialStatus(lineItem.id, material.id, status.status)
              }
              onRemove={() =>
                removeMaterialFromLineItem(lineItem.id, material.id)
              }
              onUpdateQuantity={(quantity) =>
                updateLineItemMaterialQuantity(
                  lineItem.id,
                  material.id,
                  quantity
                )
              }
              onEdit={() => setEditingMaterialId(material.id)}
            />
          ))}

        {filteredMaterials.length === 0 && (
          <Text type="secondary">
            No {accessories ? 'accessories' : 'materials'} found matching your
            search.
          </Text>
        )}

        {lineItem.materials && lineItem.materials.length > 0 && (
          <MaterialTotal
            accessories={accessories}
            total={totalMaterialsSell}
            margin={totalMargin}
          />
        )}
      </div>

      <MaterialEditDrawer
        accessories={accessories}
        materialId={editingMaterialId}
        onClose={() => setEditingMaterialId(null)}
      />
      <Drawer
        placement="right"
        width={600}
        open={addingNewMaterial}
        onClose={() => setAddingNewMaterial(false)}
      >
        {accessories ? (
          <AccessoriesForm onSuccess={() => setAddingNewMaterial(false)} />
        ) : (
          <MaterialForm onSuccess={() => setAddingNewMaterial(false)} />
        )}
      </Drawer>
    </>
  )
}
